import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button, Card, Input, useToast } from '@caruso/ui';
import { Plus, Search, Edit, Trash2, Users, Shield } from 'lucide-react';
import { userGroupsService, UserGroup } from '@/services/user-groups.service';
import { cn } from '@caruso/ui';

export function UserGroupsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const { data: groups, isLoading } = useQuery({
    queryKey: ['user-groups'],
    queryFn: userGroupsService.getAll,
  });

  const deleteMutation = useMutation({
    mutationFn: userGroupsService.delete,
    onSuccess: () => {
      toast({
        title: 'Grupo excluído',
        description: 'Grupo removido com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['user-groups'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao excluir',
        description: error.response?.data?.message || 'Não foi possível excluir o grupo',
        variant: 'destructive',
      });
    },
  });

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Tem certeza que deseja excluir o grupo "${name}"?`)) return;
    deleteMutation.mutate(id);
  };

  const filteredGroups = groups?.filter((group) => {
    const searchLower = search.toLowerCase();
    return (
      group.name.toLowerCase().includes(searchLower) ||
      group.description?.toLowerCase().includes(searchLower)
    );
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-brand-smoke rounded w-48"></div>
          <div className="h-32 bg-brand-smoke rounded"></div>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-brand-smoke rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white">Grupos de Usuários</h1>
          <p className="text-brand-ivory/80 mt-2 text-lg">
            Gerencie grupos e permissões de acesso ao sistema
          </p>
        </div>
        <Button
          onClick={() => navigate('/app/grupos/novo')}
          className="bg-brand-gold text-brand-ink hover:bg-brand-gold/90 font-semibold shadow-lg"
        >
          <Plus className="w-5 h-5 mr-2" />
          Novo Grupo
        </Button>
      </div>

      {/* Search */}
      <Card className="p-6 bg-brand-smoke border-brand-gold/20">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-gold/50" />
          <Input
            type="text"
            placeholder="Buscar grupos por nome ou descrição..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-11 text-base"
          />
        </div>
      </Card>

      {/* Groups List */}
      <div className="space-y-4">
        {filteredGroups && filteredGroups.length > 0 ? (
          filteredGroups.map((group) => (
            <motion.div
              key={group.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="p-6 bg-brand-smoke border-brand-gold/20 hover:border-brand-gold/40 transition-all">
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  {/* Group Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-4 mb-3">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg flex-shrink-0">
                        <Shield className="w-7 h-7 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-2xl font-bold text-white mb-1">{group.name}</h3>
                        {group.description && (
                          <p className="text-base text-brand-ivory/70 line-clamp-2">
                            {group.description}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3 mt-4">
                      <span
                        className={cn(
                          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium',
                          group.active
                            ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                            : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                        )}
                      >
                        {group.active ? 'Ativo' : 'Inativo'}
                      </span>

                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-brand-gold/20 text-brand-gold border border-brand-gold/30">
                        <Users className="w-3.5 h-3.5" />
                        {group._count?.users || 0} {group._count?.users === 1 ? 'usuário' : 'usuários'}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/app/grupos/${group.id}`)}
                      className="border-brand-gold/30 text-brand-gold hover:bg-brand-gold/10"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(group.id, group.name)}
                      disabled={deleteMutation.isPending}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Excluir
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))
        ) : (
          <Card className="p-12 bg-brand-smoke border-brand-gold/20">
            <div className="text-center">
              <Shield className="w-16 h-16 mx-auto mb-4 text-brand-gold/30" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {search ? 'Nenhum grupo encontrado' : 'Nenhum grupo cadastrado'}
              </h3>
              <p className="text-brand-ivory/60 mb-6">
                {search
                  ? 'Tente ajustar os termos da busca'
                  : 'Comece criando seu primeiro grupo de permissões'}
              </p>
              {!search && (
                <Button
                  onClick={() => navigate('/app/grupos/novo')}
                  className="bg-brand-gold text-brand-ink hover:bg-brand-gold/90"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Criar Grupo
                </Button>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
